import { FC, ReactElement, useEffect } from 'react';
import { useSwiper } from 'swiper/react';

import { checkIsNewActiveElement } from '../utils/checkIsNewActiveElement';
import { isHiddenElement } from '../utils/isHiddenElement';

const FOCUS_ACTIVE_ELEMENT_DELAY_MS = 100;

export const CarouselItem: FC<{
  index: number;
  children: ReactElement;
  variant: 'fixed' | 'fluid' | 'single';
  slidesPerView: number;
}> = ({ index, children, variant, slidesPerView }) => {
  const swiper = useSwiper();
  const { activeIndex, previousIndex, params, slides, isEnd } = swiper;

  const setAttributesOnHiddenElement = (element, subItems): void => {
    element.setAttribute('aria-hidden', 'true');
    subItems.forEach((item) => {
      item.setAttribute('tabIndex', '-1');
      item.setAttribute('aria-hidden', 'true');
      // eslint-disable-next-line no-param-reassign
      item.style.pointerEvents = 'none';
    });
  };

  useEffect(() => {
    const currentItem = slides?.[index] as HTMLElement;

    const activeEl = currentItem?.querySelector<HTMLElement>('a, button');
    if (
      checkIsNewActiveElement({
        activeEl,
        activeIndex,
        index,
        previousIndex,
        slidesPerView,
        slidesLength: slides.length,
      })
    ) {
      setTimeout(() => {
        activeEl!.focus();
      }, FOCUS_ACTIVE_ELEMENT_DELAY_MS);
    }

    const isFakeVisible = isHiddenElement({
      slidesPerView,
      elementIndex: index,
      variant,
      // @ts-expect-error
      // visibleSlidesIndexes is present in swiper object
      visibleSlidesIndexes: swiper.visibleSlidesIndexes,
      isEnd,
      activeIndex,
    });

    if (
      currentItem &&
      (!currentItem.className.includes('swiper-slide-visible') || isFakeVisible)
    ) {
      const focusableItems =
        currentItem.querySelectorAll<HTMLElement>('a, button, article');
      setAttributesOnHiddenElement(currentItem, focusableItems);

      return () => {
        currentItem.removeAttribute('aria-hidden');
        focusableItems.forEach((item) => {
          item.removeAttribute('tabIndex');
          item.removeAttribute('aria-hidden');
          // eslint-disable-next-line no-param-reassign
          item.style.pointerEvents = 'auto';
        });
      };
    }

    return undefined;
  }, [
    activeIndex,
    index,
    params,
    previousIndex,
    slides,
    slidesPerView,
    variant,
    isEnd,
  ]);

  return children;
};
